import { reactive } from 'vue'

import { AxiosError, AxiosHeaders, type AxiosInstance, type AxiosResponse } from 'axios'

import { StorefrontErrorHeader, StorefrontErrorType } from '@/types/StorefrontErrorType'

/** Failure the order upsert (`POST /order/{cartId}`) answers with, in place of the server. */
export enum OrderUpsertFault {
  Off = 'off',
  /** Avalara: ZIP doesn't match state. 400 with both error headers. */
  AddressZipState = 'addressZipState',
  /** Avalara: address can't be verified. 400 with the type header only. */
  AddressUnverifiable = 'addressUnverifiable',
  /** An ordinary 400 with no error headers. */
  PlainBadRequest = 'plainBadRequest',
  /** Tax can't be calculated. 500, so `axios-retry` retries it. */
  TaxUnavailable = 'taxUnavailable'
}

/** API faults set from the DevDrawer. In memory only, so a reload always clears them. */
export const devApiFaults = reactive({
  /** Every request fails with a network error before it is sent. */
  offline: false,
  /** Delay added before each request is sent. */
  latencyMs: 0,
  /** The next response that succeeds is discarded and surfaces as a network error. */
  dropNextResponse: false,
  /**
   * Every order upsert fails this way, unsent, until set back to Off. Sticky rather than one-shot
   * so the retries of the tax 500 fail too, as they would during a real outage.
   */
  orderUpsertFault: OrderUpsertFault.Off
})

export function hasActiveDevApiFaults(): boolean {
  return (
    devApiFaults.offline ||
    devApiFaults.latencyMs > 0 ||
    devApiFaults.dropNextResponse ||
    devApiFaults.orderUpsertFault !== OrderUpsertFault.Off
  )
}

const ORDER_UPSERT_URL = /^\/order\/[^/]+$/

const addressUnresolvable = (body: string, fields?: string) => ({
  status: 400,
  data: body,
  headers: {
    [StorefrontErrorHeader.ErrorType]: StorefrontErrorType.AddressUnresolvable,
    ...(fields ? { [StorefrontErrorHeader.ErrorFields]: fields } : {})
  }
})

// Bodies and headers as Sunny16 sends them.
const ORDER_UPSERT_FAULT_RESPONSES: Record<
  Exclude<OrderUpsertFault, OrderUpsertFault.Off>,
  { status: number; data: string; headers: Record<string, string> }
> = {
  [OrderUpsertFault.AddressZipState]: addressUnresolvable(
    "The ZIP code and state of that shipping address don't match. Please check them and try again.",
    'postalCode,state'
  ),
  [OrderUpsertFault.AddressUnverifiable]: addressUnresolvable(
    "We couldn't verify that shipping address. Please check the street address, city, state and ZIP code."
  ),
  [OrderUpsertFault.PlainBadRequest]: {
    status: 400,
    data: 'DevDrawer: a plain 400 from the order upsert.',
    headers: {}
  },
  [OrderUpsertFault.TaxUnavailable]: {
    status: 500,
    data: 'Unable to calculate tax for this order.',
    headers: {}
  }
}

/**
 * Installs the fault interceptors. Call before `axiosRetry` and the error-reporting interceptor:
 * request interceptors run in reverse order of registration and response interceptors in order, so
 * an injected failure then goes through retry and reporting the way a real network failure does.
 */
export function installDevApiFaults(instance: AxiosInstance): void {
  instance.interceptors.request.use(async (config) => {
    if (devApiFaults.latencyMs > 0) {
      await new Promise((resolve) => setTimeout(resolve, devApiFaults.latencyMs))
    }
    if (devApiFaults.offline) {
      throw new AxiosError('Network Error (DevDrawer: offline)', AxiosError.ERR_NETWORK, config)
    }
    if (
      devApiFaults.orderUpsertFault !== OrderUpsertFault.Off &&
      config.method === 'post' &&
      ORDER_UPSERT_URL.test(config.url ?? '')
    ) {
      const { status, data, headers } = ORDER_UPSERT_FAULT_RESPONSES[devApiFaults.orderUpsertFault]
      const response: AxiosResponse = {
        status,
        statusText: '',
        data,
        headers: new AxiosHeaders(headers),
        config
      }
      throw new AxiosError(
        `Request failed with status code ${status} (DevDrawer: order upsert fault)`,
        status >= 500 ? AxiosError.ERR_BAD_RESPONSE : AxiosError.ERR_BAD_REQUEST,
        config,
        null,
        response
      )
    }
    return config
  })

  instance.interceptors.response.use((response) => {
    if (!devApiFaults.dropNextResponse) return response

    devApiFaults.dropNextResponse = false
    throw new AxiosError(
      'Network Error (DevDrawer: response dropped)',
      AxiosError.ERR_NETWORK,
      response.config,
      response.request
    )
  })
}
